import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal, Settings2, Trash2, Plus, Phone, Pencil, Eye, EyeOff, KeyRound, Users, X, ChevronRight, LogOut, ShieldCheck, ShieldOff,
} from "lucide-react";
import {
  fetchNumbers, createNumber, deleteNumber, fetchMembers, createMember, updateMember, deleteMember, ROLE_LABELS, resetMember2fa,
  fetchWhatsappMessage, updateWhatsappMessage, resetWhatsappMessage,
} from "@/lib/api";
import { DEFAULT_WHATSAPP_MESSAGES } from "@/lib/call";
import { LINE_IDS, LINE_LABELS } from "@/lib/lines";
import { LineBadge } from "@/components/LineBadge";
import { WhatsAppIcon } from "@/components/CallFlow";
import { initials } from "@/lib/format";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { StationsTab } from "@/components/settings/StationsTab";

const ROLES = ["admin", "superviseur", "agent"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PW = 12;

const roleColor = {
  admin: "text-emerald-700 bg-emerald-50 border-emerald-200",
  superviseur: "text-blue-700 bg-blue-50 border-blue-200",
  agent: "text-slate-600 bg-slate-100 border-slate-200",
};

const apiError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

const PasswordInput = ({ value, onChange, placeholder, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1">
      <Input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
        autoComplete="new-password"
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

// Full-screen form container for mobile (one field per row, Save fixed at bottom).
const MobileFormSheet = ({ open, onClose, title, children, onSave, saveLabel = "Enregistrer", saveDisabled, saving }) => {
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-bottom duration-200" data-testid="mobile-form-sheet">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-3 py-2.5 flex items-center gap-2">
        <button onClick={onClose} aria-label="Fermer" data-testid="mobile-form-close" className="h-11 w-11 inline-flex items-center justify-center rounded-full text-slate-700 active:bg-slate-100">
          <X className="h-5 w-5" />
        </button>
        <p className="font-heading font-semibold text-slate-900">{title}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">{children}</div>
      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 pb-safe">
        <button
          data-testid="mobile-form-save"
          onClick={onSave}
          disabled={saveDisabled || saving}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
};

// Shared member form fields. prefix = "member" (add) or "edit-member" (edit).
const MemberFormBody = ({ values, setValues, isEdit, prefix }) => (
  <>
    <div>
      <Label className="text-sm">Nom complet</Label>
      <Input data-testid={`${prefix}-name-input`} value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} placeholder="Ex : Paul Martin" className="mt-1" />
    </div>
    <div>
      <Label className="text-sm">E-mail</Label>
      <Input data-testid={`${prefix}-email-input`} value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} placeholder="paul.martin@exemple.fr" className="mt-1" />
      {values.email && !EMAIL_RE.test(values.email) && <p className="text-xs text-red-600 mt-1">Format d'e-mail invalide.</p>}
    </div>
    <div>
      <Label className="text-sm">{isEdit ? "Nouveau mot de passe" : "Mot de passe"}</Label>
      <PasswordInput testid={`${prefix}-password-input`} value={values.password} onChange={(e) => setValues({ ...values, password: e.target.value })} placeholder={isEdit ? "Laisser vide pour ne pas changer" : "Au moins 12 caractères"} />
      <p className={`text-xs mt-1 ${values.password && values.password.length < MIN_PW ? "text-red-600" : "text-slate-400"}`}>Minimum {MIN_PW} caractères.</p>
      {isEdit && <p className="text-xs text-slate-400 mt-1">Le mot de passe actuel n'est jamais affiché. Renseignez ce champ uniquement pour le réinitialiser.</p>}
    </div>
    <div>
      <Label className="text-sm">Rôle</Label>
      <Select value={values.role} onValueChange={(v) => setValues({ ...values, role: v })}>
        <SelectTrigger data-testid={`${prefix}-role-select`} className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </>
);

/* =======================  NUMBERS  ======================= */
// Shared WhatsApp pre-fill message, per line (admin-editable, same for all members).
const WhatsAppLineMessage = ({ line }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["whatsapp-message", line], queryFn: () => fetchWhatsappMessage(line) });
  const [draft, setDraft] = useState(null); // null = not edited locally yet
  const value = draft ?? data?.message ?? "";
  const isDefault = data?.is_default ?? true;

  const saveMut = useMutation({
    mutationFn: () => updateWhatsappMessage(value, line),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-message", line] });
      setDraft(null);
      toast.success(`Message WhatsApp enregistré (${LINE_LABELS[line]})`);
    },
    onError: (e) => toast.error(apiError(e, "Échec de l'enregistrement")),
  });
  const resetMut = useMutation({
    mutationFn: () => resetWhatsappMessage(line),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-message", line] });
      setDraft(null);
      toast.success("Message par défaut rétabli");
    },
    onError: (e) => toast.error(apiError(e, "Échec de la réinitialisation")),
  });

  return (
    <div data-testid={`whatsapp-message-${line}`} className="rounded-lg border border-slate-100 bg-slate-50/60 p-4 space-y-2">
      <LineBadge line={line} testid={`whatsapp-message-badge-${line}`} />
      <Textarea
        data-testid={`whatsapp-message-input-${line}`}
        value={value}
        onChange={(e) => setDraft(e.target.value.slice(0, 300))}
        maxLength={300}
        rows={2}
        aria-label={`Message WhatsApp ${LINE_LABELS[line]}`}
      />
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid={`whatsapp-message-reset-${line}`}
          onClick={() => resetMut.mutate()}
          disabled={isDefault || resetMut.isPending}
          className="text-xs text-emerald-700 hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
        >
          Rétablir le message par défaut
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 tabular-nums" data-testid={`whatsapp-message-count-${line}`}>{value.length}/300</span>
          <button
            type="button"
            data-testid={`whatsapp-message-save-${line}`}
            onClick={() => saveMut.mutate()}
            disabled={!value.trim() || value === data?.message || saveMut.isPending}
            className="inline-flex items-center rounded-lg bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700 transition-colors disabled:opacity-40"
          >
            Enregistrer
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Par défaut : « {DEFAULT_WHATSAPP_MESSAGES[line]} »</p>
    </div>
  );
};

const WhatsAppMessageCard = () => (
  <div data-testid="whatsapp-message-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3 max-w-2xl">
    <div className="flex items-center gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
        <WhatsAppIcon className="h-4 w-4" />
      </span>
      <div>
        <p className="font-heading font-semibold text-slate-900 text-sm">Message WhatsApp pré-rempli</p>
        <p className="text-xs text-slate-400 mt-0.5">
          Inséré automatiquement à l'ouverture d'une conversation WhatsApp, selon la ligne de l'appel. Il reste modifiable dans WhatsApp avant l'envoi — rien n'est jamais envoyé automatiquement.
        </p>
      </div>
    </div>
    {LINE_IDS.map((line) => <WhatsAppLineMessage key={line} line={line} />)}
  </div>
);

const NumbersTab = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: numbers = [] } = useQuery({ queryKey: ["numbers"], queryFn: fetchNumbers });
  const [toDelete, setToDelete] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [lineForm, setLineForm] = useState({ number: "", line_name: "" });

  const delMut = useMutation({
    mutationFn: (id) => deleteNumber(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["numbers"] }); toast.success("Numéro supprimé"); setToDelete(null); },
    onError: (e) => toast.error(apiError(e, "Échec de la suppression")),
  });

  const addMut = useMutation({
    mutationFn: () => createNumber(lineForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["numbers"] }); toast.success("Numéro ajouté"); setAddOpen(false); setLineForm({ number: "", line_name: "" }); },
    onError: (e) => toast.error(apiError(e, "Impossible d'ajouter le numéro")),
  });

  const lineValid = lineForm.number.trim() && lineForm.line_name.trim();

  return (
    <>
      {/* Desktop/tablet table (unchanged) */}
      <div className="hidden md:block rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="numbers-table">
        <table className="w-full min-w-[620px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/50">
              <th className="py-3 pl-5 pr-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Nom de la ligne</th>
              <th className="py-3 px-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Numéro</th>
              <th className="py-3 px-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Membres</th>
              <th className="py-3 pr-5 pl-3 text-right text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((n) => (
              <tr key={n.id} data-testid={`number-row-${n.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="py-3.5 pl-5 pr-3">
                  <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center"><Phone className="h-4 w-4 text-emerald-600" /></span>
                    <span className="font-medium text-slate-900 text-sm">{n.line_name}</span>
                  </div>
                </td>
                <td className="py-3.5 px-3 text-sm text-slate-600 tabular-nums whitespace-nowrap">{n.number}</td>
                <td className="py-3.5 px-3 text-sm text-slate-600">{n.member_ids.length}</td>
                <td className="py-3.5 pr-5 pl-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`number-menu-${n.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem data-testid={`number-settings-${n.id}`} onClick={() => navigate(`/parametres/numeros/${n.id}`)} className="cursor-pointer">
                        <Settings2 className="h-4 w-4 mr-2" /> Paramètres détaillés
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`number-delete-${n.id}`} onClick={() => setToDelete(n)} className="cursor-pointer text-red-600 focus:text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" /> Supprimer le numéro
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2" data-testid="numbers-cards">
        <button data-testid="add-number-btn" onClick={() => setAddOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-emerald-300 text-emerald-700 font-medium text-sm py-3 active:bg-emerald-50">
          <Plus className="h-4 w-4" /> Ajouter un numéro
        </button>
        {numbers.map((n) => (
          <div
            key={n.id}
            data-testid={`number-card-${n.id}`}
            onClick={() => navigate(`/parametres/numeros/${n.id}`)}
            className="rounded-xl border border-slate-200 bg-white shadow-sm active:bg-slate-50 transition-colors p-3 flex items-center gap-3"
          >
            <span className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Phone className="h-4 w-4 text-emerald-600" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 text-sm truncate">{n.line_name}</p>
              <p className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{n.number}</p>
            </div>
            <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">{n.member_ids.length} membre{n.member_ids.length > 1 ? "s" : ""}</span>
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`number-card-menu-${n.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 active:bg-slate-100">
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid={`number-card-edit-${n.id}`} onClick={() => navigate(`/parametres/numeros/${n.id}`)} className="cursor-pointer">
                    <Settings2 className="h-4 w-4 mr-2" /> Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`number-card-members-${n.id}`} onClick={() => navigate(`/parametres/numeros/${n.id}`)} className="cursor-pointer">
                    <Users className="h-4 w-4 mr-2" /> Gérer les membres
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`number-card-delete-${n.id}`} onClick={() => setToDelete(n)} className="cursor-pointer text-red-600 focus:text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <WhatsAppMessageCard />
      </div>

      {/* Delete confirm */}
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Supprimer le numéro</DialogTitle>
            <DialogDescription className="sr-only">Confirmer la suppression du numéro</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Voulez-vous vraiment supprimer <span className="font-semibold">{toDelete?.line_name}</span> ({toDelete?.number}) ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <button onClick={() => setToDelete(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50">Annuler</button>
            <button data-testid="confirm-delete-number" onClick={() => delMut.mutate(toDelete.id)} className="rounded-lg px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700">Supprimer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add line — mobile full-screen sheet */}
      <MobileFormSheet
        open={isMobile && addOpen}
        onClose={() => setAddOpen(false)}
        title="Ajouter un numéro"
        onSave={() => addMut.mutate()}
        saveDisabled={!lineValid}
        saving={addMut.isPending}
      >
        <div>
          <Label className="text-sm">Numéro</Label>
          <Input data-testid="line-number-input" value={lineForm.number} onChange={(e) => setLineForm({ ...lineForm, number: e.target.value })} placeholder="+33 4 23 34 06 34" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Nom de la ligne</Label>
          <Input data-testid="line-name-create-input" value={lineForm.line_name} onChange={(e) => setLineForm({ ...lineForm, line_name: e.target.value })} placeholder="Ex : Ligne commerciale" className="mt-1" />
        </div>
      </MobileFormSheet>
    </>
  );
};

/* =======================  MEMBERS  ======================= */
const MembersTab = () => {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "agent" });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["members"] });
    qc.invalidateQueries({ queryKey: ["number-members"] });
  };

  const addValid = form.name.trim() && EMAIL_RE.test(form.email) && form.password.length >= MIN_PW;
  const editEmailValid = EMAIL_RE.test(editForm.email);
  const editPwValid = editForm.password === "" || editForm.password.length >= MIN_PW;
  const editValid = editForm.name.trim() && editEmailValid && editPwValid;

  const addMut = useMutation({
    mutationFn: () => createMember(form),
    onSuccess: () => { invalidate(); toast.success("Membre ajouté"); setAddOpen(false); setForm({ name: "", email: "", password: "", role: "agent" }); },
    onError: (e) => toast.error(apiError(e, "Impossible d'ajouter le membre")),
  });
  const roleMut = useMutation({
    mutationFn: ({ id, role }) => updateMember(id, { role }),
    onSuccess: () => { invalidate(); toast.success("Rôle mis à jour"); },
    onError: (e) => toast.error(apiError(e, "Échec de la mise à jour")),
  });
  const delMut = useMutation({
    mutationFn: (id) => deleteMember(id),
    onSuccess: () => { invalidate(); toast.success("Membre retiré"); },
    onError: (e) => toast.error(apiError(e, "Échec de la suppression")),
  });
  const reset2faMut = useMutation({
    mutationFn: (id) => resetMember2fa(id),
    onSuccess: () => { invalidate(); toast.success("2FA réinitialisée — le membre devra la reconfigurer"); },
    onError: (e) => toast.error(apiError(e, "Échec de la réinitialisation")),
  });
  const editMut = useMutation({
    mutationFn: () => {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      return updateMember(editing.id, payload);
    },
    onSuccess: () => { invalidate(); toast.success(editForm.password ? "Membre et mot de passe mis à jour" : "Membre mis à jour"); setEditing(null); },
    onError: (e) => toast.error(apiError(e, "Échec de la modification")),
  });

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({ name: m.name, email: m.email, password: "", role: m.role });
  };

  return (
    <div className="space-y-4">
      {/* Desktop add button */}
      <div className="hidden md:flex justify-end">
        <button data-testid="add-member-btn" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-4 py-2 hover:bg-emerald-700 transition-colors">
          <Plus className="h-4 w-4" /> Ajouter un membre
        </button>
      </div>

      {/* Desktop/tablet table (no password column) */}
      <div className="hidden md:block rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="members-table">
        <table className="w-full min-w-[620px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/50">
              <th className="py-3 pl-5 pr-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Membre</th>
              <th className="py-3 px-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">E-mail</th>
              <th className="py-3 px-3 text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Rôle</th>
              <th className="py-3 pr-5 pl-3 text-right text-xs uppercase tracking-[0.05em] font-semibold text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="py-3.5 pl-5 pr-3">
                  <div className="flex items-center gap-2.5">
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name} className="h-8 w-8 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <span className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-500">{initials(m.name)}</span>
                    )}
                    <span className="font-medium text-slate-900 text-sm">{m.name}</span>
                    {m.totp_enabled && (
                      <span data-testid={`member-2fa-badge-${m.id}`} title="Double authentification activée" className="inline-flex items-center text-emerald-600">
                        <ShieldCheck className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3.5 px-3 text-sm text-slate-600">{m.email}</td>
                <td className="py-3.5 px-3">
                  <Select value={m.role} onValueChange={(v) => roleMut.mutate({ id: m.id, role: v })}>
                    <SelectTrigger data-testid={`member-role-${m.id}`} className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-3.5 pr-5 pl-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button data-testid={`edit-member-${m.id}`} onClick={() => openEdit(m)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700 font-medium">
                      <Pencil className="h-4 w-4" /> Modifier
                    </button>
                    {m.totp_enabled && m.role !== "admin" && (
                      <button data-testid={`reset-2fa-${m.id}`} onClick={() => reset2faMut.mutate(m.id)} className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium">
                        <ShieldOff className="h-4 w-4" /> Réinit. 2FA
                      </button>
                    )}
                    <button data-testid={`remove-member-${m.id}`} onClick={() => delMut.mutate(m.id)} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium">
                      <Trash2 className="h-4 w-4" /> Retirer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2" data-testid="members-cards">
        <button data-testid="add-member-btn-mobile" onClick={() => setAddOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-emerald-300 text-emerald-700 font-medium text-sm py-3 active:bg-emerald-50">
          <Plus className="h-4 w-4" /> Ajouter un membre
        </button>
        {members.map((m) => (
          <div key={m.id} data-testid={`member-card-${m.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 flex items-center gap-3">
            {m.avatar ? (
              <img src={m.avatar} alt={m.name} className="h-10 w-10 rounded-full object-cover border border-slate-200 shrink-0" />
            ) : (
              <span className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 shrink-0">{initials(m.name)}</span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-slate-900 text-sm truncate">{m.name}</p>
                {m.totp_enabled && (
                  <ShieldCheck data-testid={`member-2fa-badge-${m.id}`} className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                )}
              </div>
              <p className="text-xs text-slate-400 truncate">{m.email}</p>
            </div>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border shrink-0 ${roleColor[m.role] || roleColor.agent}`}>
              {ROLE_LABELS[m.role] || m.role}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`member-card-menu-${m.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 active:bg-slate-100 shrink-0">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid={`member-card-edit-${m.id}`} onClick={() => openEdit(m)} className="cursor-pointer">
                  <Pencil className="h-4 w-4 mr-2" /> Modifier
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`member-card-resetpw-${m.id}`} onClick={() => openEdit(m)} className="cursor-pointer">
                  <KeyRound className="h-4 w-4 mr-2" /> Réinitialiser le mot de passe
                </DropdownMenuItem>
                {m.totp_enabled && m.role !== "admin" && (
                  <DropdownMenuItem data-testid={`member-card-reset2fa-${m.id}`} onClick={() => reset2faMut.mutate(m.id)} className="cursor-pointer">
                    <ShieldOff className="h-4 w-4 mr-2" /> Réinitialiser la 2FA
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid={`member-card-remove-${m.id}`} onClick={() => delMut.mutate(m.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" /> Retirer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* Add member — desktop dialog OR mobile sheet */}
      {isMobile ? (
        <MobileFormSheet open={addOpen} onClose={() => setAddOpen(false)} title="Nouveau membre" saveLabel="Ajouter" onSave={() => addMut.mutate()} saveDisabled={!addValid} saving={addMut.isPending}>
          <MemberFormBody values={form} setValues={setForm} isEdit={false} prefix="member" />
        </MobileFormSheet>
      ) : (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Nouveau membre</DialogTitle>
              <DialogDescription className="sr-only">Ajouter un nouveau membre à l'équipe</DialogDescription>
            </DialogHeader>
            <div className="space-y-3"><MemberFormBody values={form} setValues={setForm} isEdit={false} prefix="member" /></div>
            <DialogFooter>
              <button data-testid="save-member-btn" disabled={!addValid || addMut.isPending} onClick={() => addMut.mutate()} className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Ajouter</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit member — desktop dialog OR mobile sheet */}
      {isMobile ? (
        <MobileFormSheet open={!!editing} onClose={() => setEditing(null)} title="Modifier le membre" onSave={() => editMut.mutate()} saveDisabled={!editValid} saving={editMut.isPending}>
          <MemberFormBody values={editForm} setValues={setEditForm} isEdit={true} prefix="edit-member" />
        </MobileFormSheet>
      ) : (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifier le membre</DialogTitle>
              <DialogDescription className="sr-only">Modifier les informations et identifiants du membre</DialogDescription>
            </DialogHeader>
            <div className="space-y-3"><MemberFormBody values={editForm} setValues={setEditForm} isEdit={true} prefix="edit-member" /></div>
            <DialogFooter>
              <button onClick={() => setEditing(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50">Annuler</button>
              <button data-testid="save-edit-member-btn" disabled={!editValid || editMut.isPending} onClick={() => editMut.mutate()} className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Enregistrer</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const Settings = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/connexion", { replace: true });
  };
  return (
    <div data-testid="settings-page" className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">Paramètres</h1>
          <p className="text-sm text-slate-500 mt-1">Gérez vos numéros et les membres de votre équipe.</p>
        </div>
        <button
          type="button"
          data-testid="logout-button"
          onClick={doLogout}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-3 sm:px-4 py-2 hover:border-red-300 hover:text-red-700 transition-colors shrink-0"
        >
          <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Se déconnecter</span>
        </button>
      </header>

      <Tabs defaultValue="numbers">
        <TabsList data-testid="settings-tabs" className="grid grid-cols-3 w-full md:inline-flex md:w-auto">
          <TabsTrigger value="numbers" data-testid="tab-numbers" className="w-full md:w-auto">Numéros</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="w-full md:w-auto">Membres</TabsTrigger>
          <TabsTrigger value="stations" data-testid="tab-stations" className="w-full md:w-auto">Postes</TabsTrigger>
        </TabsList>
        <TabsContent value="numbers" className="mt-5"><NumbersTab /></TabsContent>
        <TabsContent value="members" className="mt-5"><MembersTab /></TabsContent>
        <TabsContent value="stations" className="mt-5"><StationsTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
